import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/custom-select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useProjectMeta } from '@/hooks/meta/useProjectMeta';
import { getTemplateName, STORY_TEMPLATES, StoryTemplate } from '@/lib/storyTemplates';
import { templateService } from '@/services/template';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TimelineDetails, TimelinePreview } from './TimeLinePreview';

interface CreateTimelineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (timelineId: number) => void;
}

const convertStoryTemplateToTimelineDetails = (template: StoryTemplate): TimelineDetails => {
  return {
    name: template.name,
    description: template.description,
    beats: template.beats,
    recommendedChapters: template.recommendedChapters
  };
};

export const CreateTimelineDialog: React.FC<CreateTimelineDialogProps> = ({
  open,
  onOpenChange,
  onCreated
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { projectId } = useParams();
  // Use minimal data for better performance - we only need the ID
  const { currentStoryId } = useProjectMeta(projectId || null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Reset form state when dialog opens
  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setSelectedTemplate(null);
      setIsCreating(false);
    }
  }, [open]);

  const handleCreateTimeline = async () => {
    if (!title) {
      toast({
        title: 'Error',
        description: 'Timeline title is required',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsCreating(true);

      // Get the current story ID from the hook
      const effectiveStoryId = currentStoryId || '';

      // Log detailed info before creating the timeline
      console.log("Creating timeline with params:", {
        currentStoryId: currentStoryId,
        effectiveStoryId,
        title,
        description,
        templateToUse: selectedTemplate === "none" ? "empty" : (selectedTemplate || "empty")
      });

      // Create timeline
      const templateToUse = selectedTemplate === "none" ? "empty" : (selectedTemplate || "empty");

      // Validate storyId
      if (!effectiveStoryId) {
        console.warn("Warning: No valid story ID available, timeline might not associate correctly with project");
      }

      const result = await templateService.createTimelineFromTemplate(
        templateToUse, // Use empty if selectedTemplate is none or null
        title,
        description,
        effectiveStoryId // Use the effective storyId from currentStory
      );

      // Log the result for debugging
      console.log("Create timeline result:", result);

      if (!result.success || !result.timeline) {
        throw new Error(result.error || "Failed to create timeline");
      }

      const timelineId = result.timeline.id;

      console.log("Timeline created successfully with ID:", timelineId);

      toast({
        title: 'Timeline Created',
        description: selectedTemplate && selectedTemplate !== "none"
          ? `Successfully created timeline with the "${getTemplateName(selectedTemplate)}" template.`
          : 'Successfully created timeline.',
      });

      // Close dialog
      onOpenChange(false);

      // Callback or redirect
      if (onCreated) {
        console.log("Calling onCreated with timelineId:", timelineId);
        onCreated(timelineId);
      } else {
        console.log("Navigating to timeline page:", timelineId);
        navigate(`/timeline/${timelineId}`);
      }

    } catch (error) {
      console.error('Error creating timeline:', error);
      toast({
        title: 'Error Creating Timeline',
        description: error instanceof Error ? error.message : 'Failed to create timeline. Please try again.',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
          <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
            <DialogTitle className="text-2xl font-semibold text-foreground tracking-tight">Create New Timeline</DialogTitle>
            <DialogDescription className="text-base text-muted-foreground leading-relaxed">
              Create a new timeline for your story or project
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 px-8 py-8">
            {/* Title */}
            <div className="space-y-2.5">
              <Label htmlFor="title" className="text-sm font-medium text-foreground">
                Title
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="My Story Timeline"
                className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm"
              />
            </div>

            {/* Description */}
            <div className="space-y-2.5">
              <Label htmlFor="description" className="text-sm font-medium text-foreground">
                Description (Optional)
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of your timeline"
                rows={2}
                className="min-h-[100px] rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all text-foreground placeholder:text-muted-foreground/50 shadow-sm resize-none"
              />
            </div>

            {/* Template Selection */}
            <div className="space-y-2.5">
              <Label htmlFor="template" className="text-sm font-medium text-foreground">
                Template (Optional)
              </Label>
              <Select
                value={selectedTemplate || "none"}
                onValueChange={(value) => {
                  // Convert "none" to null internally for consistency with existing code
                  setSelectedTemplate(value === "none" ? null : value);
                }}
              >
                <SelectTrigger id="template" className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground shadow-sm">
                  <SelectValue placeholder="Select a storytelling template or leave empty" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-border/40 shadow-xl bg-popover/95 backdrop-blur-sm">
                  <SelectItem value="none">None (Blank Timeline)</SelectItem>
                  <SelectItem value="hero">Hero's Journey</SelectItem>
                  <SelectItem value="savecat">Save the Cat</SelectItem>
                  <SelectItem value="threeact">Three-Act Structure</SelectItem>
                  <SelectItem value="storycircle">Story Circle</SelectItem>
                  <SelectItem value="sevenpoint">Seven Point Story Structure</SelectItem>
                  <SelectItem value="mystery">Mystery/Detective</SelectItem>
                  <SelectItem value="romance">Romance Arc</SelectItem>
                  <SelectItem value="romancingbeat">Romancing the Beat</SelectItem>
                  <SelectItem value="snowflake">Snowflake Method</SelectItem>
                  <SelectItem value="storygenius">Story Genius Method</SelectItem>
                  <SelectItem value="fichtean">Fichtean Curve</SelectItem>
                  <SelectItem value="shortjourney">Hero's Journey for Short Stories</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Templates create chapters and plot cards based on established narrative frameworks
              </p>
            </div>

            {/* Template Preview */}
            {selectedTemplate && selectedTemplate !== "none" && (
              <TimelinePreview
                timelineDetails={convertStoryTemplateToTimelineDetails(STORY_TEMPLATES[selectedTemplate])}
              />
            )}
          </div>

          <DialogFooter className="p-6 bg-muted/5 border-t border-border/40">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isCreating}
              className="min-w-[100px] rounded-xl hover:bg-muted/50"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={!title || isCreating}
              onClick={handleCreateTimeline}
              className="min-w-[120px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base"
            >
              {isCreating ? (
                <>Creating...</>
              ) : (
                <>Create Timeline</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};